import { AuthContext, BaseNavLink, SidebarDivider, isDemoModeActive } from '@enter/core';
import {
    mdiCheckCircleOutline,
    mdiClipboardTextOutline,
    mdiRobotOutline,
    mdiCogOutline,
    mdiEmailOutline,
    mdiFax,
    mdiPhoneOutline,
    mdiChartLine,
    mdiFileDocumentOutline,
    mdiHandshakeOutline,
    mdiOfficeBuildingOutline,
    mdiChevronUp,
    mdiChevronDown,
    mdiClipboardCheckOutline,
    mdiCodeBraces,
    mdiOpenInNew,
    mdiEyeOutline,
    mdiEyeOffOutline,
    mdiCashMultiple,
} from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import { IInjectedRootStore } from 'stores';
import * as S from './main.sidebar.styles';

type IMainSidebarProps = IInjectedRootStore;

export const MainSidebar: React.FunctionComponent<IMainSidebarProps> = inject('rootStore')(
    observer((props: IMainSidebarProps): React.ReactElement => {
        const { rootStore } = props;
        const { messageStore } = rootStore;
        const { refreshOpenInboxCount, openInboxTotal } = messageStore;
        const context = useContext(AuthContext);
        const params = useParams();
        const accountId = params.accountId;

        const isMailRoute = useMatch(accountId ? `/${accountId}/mail/*` : 'mail/*');
        const isFaxRoute = useMatch(accountId ? `/${accountId}/fax/*` : 'fax/*');
        const isContractsRoute = useMatch(accountId ? `/${accountId}/chargemasters/*` : 'chargemasters/*');
        const isDocumentsRoute = useMatch(accountId ? `/${accountId}/documents` : 'documents');
        const isPhoneRoute = useMatch(accountId ? `/${accountId}/phone` : 'phone');
        const isPatientCollectionsRoute = useMatch(
            accountId ? `/${accountId}/patient-collections/*` : 'patient-collections/*',
        );
        const isFileCabinet =
            !!isMailRoute || !!isFaxRoute || !!isContractsRoute || !!isDocumentsRoute || !!isPhoneRoute;
        const [clickedFileCabinet, setClickedFileCabinet] = useState<boolean | null>(null);
        const showFileCabinet = clickedFileCabinet !== null ? clickedFileCabinet : isFileCabinet;
        const toggleFileCabinet = () => {
            setClickedFileCabinet(prev => {
                const newValue = prev === null ? !isFileCabinet : !prev;
                return newValue;
            });
        };

        // Reset File Cabinet manual toggle when route changes
        useEffect(() => {
            setClickedFileCabinet(null);
        }, [isMailRoute, isFaxRoute, isContractsRoute, isDocumentsRoute, isPhoneRoute]);

        const isSettingsOverviewRoute = useMatch(accountId ? `/${accountId}/settings/*` : 'settings/*');
        const isPayersProvidersRoute = useMatch(
            accountId ? `/${accountId}/payers-providers` : 'payers-providers',
        );
        const isSettingsSection = !!isSettingsOverviewRoute || !!isPayersProvidersRoute;
        const [clickedSettings, setClickedSettings] = useState<boolean | null>(null);
        const showSettings = clickedSettings !== null ? clickedSettings : isSettingsSection;
        const toggleSettings = () => {
            setClickedSettings(prev => {
                const newValue = prev === null ? !isSettingsSection : !prev;
                return newValue;
            });
        };

        useEffect(() => {
            setClickedSettings(null);
        }, [isSettingsOverviewRoute, isPayersProvidersRoute]);

        const [showUniversityPassword, setShowUniversityPassword] = useState(false);
        const [copied, setCopied] = useState(false);
        const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const universityPassword = 'entertheuni';
        const copyUniversityPassword = (): void => {
            try {
                if (navigator?.clipboard?.writeText) {
                    navigator.clipboard.writeText(universityPassword);
                    setCopied(true);
                    if (copyTimeoutRef.current) {
                        clearTimeout(copyTimeoutRef.current);
                    }
                    copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
                    return;
                }
                const textarea = document.createElement('textarea');
                textarea.value = universityPassword;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                setCopied(true);
                if (copyTimeoutRef.current) {
                    clearTimeout(copyTimeoutRef.current);
                }
                copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
            } catch {
                // ignore copy failures
            }
        };

        useEffect(
            () => () => {
                if (copyTimeoutRef.current) {
                    clearTimeout(copyTimeoutRef.current);
                }
            },
            [],
        );

        // NB: setup background poll to check for messages
        // Initial call on mount to show count immediately
        // Background poll updates the count every 3 minutes
        useEffect(() => {
            try {
                context.cancelGroup && context.cancelGroup('messaging');
                context.setGroup && context.setGroup('messaging');
                refreshOpenInboxCount();
                const updateInterval = 3 * 60 * 1000;
                const interval = setInterval(refreshOpenInboxCount, updateInterval);
                return (): void => clearInterval(interval);
            } catch (err) {
                // bury exceptions
            }
        }, [context, refreshOpenInboxCount]);

        const demoModeEnabled = isDemoModeActive();

        return (
            <S.SideBar
                visible={!rootStore.layoutStore.sidebarCollapsed}
                onClose={() => rootStore.layoutStore.toggleSidebar(true)}>
                <SidebarDivider />
                <BaseNavLink to={accountId ? `/${accountId}/claims` : 'claims'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active}>
                            <S.MenuIcon path={mdiClipboardTextOutline} size={1} />
                            Claims
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                <SidebarDivider />
                {/*
                 * Top level, directly under Claims — NOT in the File Cabinet.
                 *
                 * It shipped in the File Cabinet because Jordan's SUPPORT design puts it there,
                 * beside the other reference data. That inference was wrong for this app: a
                 * provider opens Payers to answer "where did my claim go", which is a question
                 * about their claims, not a lookup table they browse. John Russell and Aleksandr
                 * Tynianov both went looking for it here and could not find it.
                 */}
                <BaseNavLink to={accountId ? `/${accountId}/payers` : 'payers'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active}>
                            <S.MenuIcon path={mdiOfficeBuildingOutline} size={1} />
                            Payers
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                <SidebarDivider />
                <BaseNavLink to={accountId ? `/${accountId}/messaging` : 'messaging'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active}>
                            <S.MenuIcon path={mdiCheckCircleOutline} size={1} />
                            Tasks {openInboxTotal > 0 && <S.MessageCount>{openInboxTotal}</S.MessageCount>}
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                <SidebarDivider />
                <BaseNavLink to={accountId ? `/${accountId}/reports` : 'reports'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active}>
                            <S.MenuIcon path={mdiChartLine} size={1} />
                            Reports
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                <SidebarDivider />
                <BaseNavLink to={accountId ? `/${accountId}/configuration` : 'configuration'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active}>
                            <S.MenuIcon path={mdiRobotOutline} size={1} />
                            Workers
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                <SidebarDivider />
                <BaseNavLink to={accountId ? `/${accountId}/coders` : 'coders'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active}>
                            <S.MenuIcon path={mdiCodeBraces} size={1} />
                            Coders
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                <SidebarDivider />
                <BaseNavLink to={accountId ? `/${accountId}/eligibility` : 'eligibility'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active}>
                            <S.MenuIcon path={mdiClipboardCheckOutline} size={1} />
                            Eligibility
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                <SidebarDivider />
                <BaseNavLink to={accountId ? `/${accountId}/patient-collections/needs` : 'patient-collections/needs'}>
                    {(linkProps): React.ReactElement => (
                        <S.StyledSidebarItem active={linkProps.active || !!isPatientCollectionsRoute}>
                            <S.MenuIcon path={mdiCashMultiple} size={1} />
                            Patient Collections
                        </S.StyledSidebarItem>
                    )}
                </BaseNavLink>
                {/* Denials & Rejections is hidden until the reworked page is vetted (FE-116).
                    Jordan Kelley, 2026-09-30: "the Rejections / Denials page is NOT finished. can we
                    please remove it from the platform until it is fully vetted?" The page, its store
                    and its route are all still here — restore this link and the route in `main.tsx`
                    together. */}
                <SidebarDivider />
                <S.StyledSidebarItem onClick={toggleFileCabinet}>
                    <S.MenuIcon path={mdiFileDocumentOutline} size={1} />
                    <span>File Cabinet</span>
                    <S.CollapseButton
                        variant="text"
                        onClick={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFileCabinet();
                        }}>
                        <S.CollapseIcon
                            path={showFileCabinet ? mdiChevronUp : mdiChevronDown}
                            size={1}
                            open={showFileCabinet}
                        />
                    </S.CollapseButton>
                </S.StyledSidebarItem>
                <S.SidebarCollapse in={showFileCabinet} timeout="auto" unmountOnExit={false}>
                    <BaseNavLink to={accountId ? `/${accountId}/mail/inbox` : 'mail/inbox'}>
                        {(linkProps): React.ReactElement => (
                            <S.CollapseSidebarItem active={linkProps.active}>
                                <S.CollapseIndent />
                                <S.SubMenuIcon path={mdiEmailOutline} size={0.9} />
                                Mail
                            </S.CollapseSidebarItem>
                        )}
                    </BaseNavLink>
                    <BaseNavLink to={accountId ? `/${accountId}/fax/inbox` : 'fax/inbox'}>
                        {(linkProps): React.ReactElement => (
                            <S.CollapseSidebarItem active={linkProps.active}>
                                <S.CollapseIndent />
                                <S.SubMenuIcon path={mdiFax} size={0.9} />
                                Fax
                            </S.CollapseSidebarItem>
                        )}
                    </BaseNavLink>
                    <BaseNavLink to={accountId ? `/${accountId}/chargemasters` : 'chargemasters'}>
                        {(linkProps): React.ReactElement => (
                            <S.CollapseSidebarItem active={linkProps.active}>
                                <S.CollapseIndent />
                                <S.SubMenuIcon path={mdiHandshakeOutline} size={0.9} />
                                Contracts
                            </S.CollapseSidebarItem>
                        )}
                    </BaseNavLink>
                    <BaseNavLink to={accountId ? `/${accountId}/documents` : 'documents'}>
                        {(linkProps): React.ReactElement => (
                            <S.CollapseSidebarItem active={linkProps.active}>
                                <S.CollapseIndent />
                                <S.SubMenuIcon path={mdiFileDocumentOutline} size={0.9} />
                                Documents
                            </S.CollapseSidebarItem>
                        )}
                    </BaseNavLink>
                    <BaseNavLink to={accountId ? `/${accountId}/phone` : 'phone'}>
                        {(linkProps): React.ReactElement => (
                            <S.CollapseSidebarItem active={linkProps.active}>
                                <S.CollapseIndent />
                                <S.SubMenuIcon path={mdiPhoneOutline} size={0.9} />
                                Phone
                            </S.CollapseSidebarItem>
                        )}
                    </BaseNavLink>
                </S.SidebarCollapse>
                <SidebarDivider />
                <S.StyledSidebarItem onClick={toggleSettings}>
                    <S.MenuIcon path={mdiCogOutline} size={1} />
                    <span>Settings</span>
                    <S.CollapseButton
                        variant="text"
                        onClick={(e): void => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSettings();
                        }}>
                        <S.CollapseIcon
                            path={showSettings ? mdiChevronUp : mdiChevronDown}
                            size={1}
                            open={showSettings}
                        />
                    </S.CollapseButton>
                </S.StyledSidebarItem>
                <S.SidebarCollapse in={showSettings} timeout="auto" unmountOnExit={false}>
                    <BaseNavLink to={accountId ? `/${accountId}/settings/overview` : 'settings/overview'}>
                        {(linkProps): React.ReactElement => (
                            <S.CollapseSidebarItem active={linkProps.active}>
                                <S.CollapseIndent />
                                <S.SubMenuIcon path={mdiCogOutline} size={0.9} />
                                Overview
                            </S.CollapseSidebarItem>
                        )}
                    </BaseNavLink>
                    <BaseNavLink to={accountId ? `/${accountId}/payers-providers` : 'payers-providers'}>
                        {(linkProps): React.ReactElement => (
                            <S.CollapseSidebarItem active={linkProps.active}>
                                <S.CollapseIndent />
                                <S.SubMenuIcon path={mdiHandshakeOutline} size={0.9} />
                                Payers Providers
                            </S.CollapseSidebarItem>
                        )}
                    </BaseNavLink>
                </S.SidebarCollapse>
                <S.Spacer />
                <S.ResourceLinks>
                    <S.ResourceLink
                        href="https://developers.enter.health/docs/welcome-to-enter"
                        target="_blank"
                        rel="noopener noreferrer">
                        <S.ResourceLinkText>Developer Docs</S.ResourceLinkText>
                        <S.ResourceLinkIcon path={mdiOpenInNew} size={0.65} />
                    </S.ResourceLink>
                    <S.ResourceDivider />
                    <S.ResourceLink
                        href="https://www.enter.health/university"
                        target="_blank"
                        rel="noopener noreferrer">
                        <S.ResourceLinkText>University</S.ResourceLinkText>
                        <S.ResourceLinkIcon path={mdiOpenInNew} size={0.65} />
                    </S.ResourceLink>
                    <S.PasswordRow>
                        <S.PasswordLabel>University Password:</S.PasswordLabel>
                        <S.PasswordValueRow>
                            <S.PasswordValue
                                clickable={showUniversityPassword}
                                role="button"
                                tabIndex={0}
                                title={showUniversityPassword ? 'Copy password' : 'Show password to copy'}
                                onClick={() => {
                                    if (!showUniversityPassword) return;
                                    copyUniversityPassword();
                                }}
                                onKeyDown={(e): void => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (!showUniversityPassword) return;
                                        copyUniversityPassword();
                                    }
                                }}>
                                {showUniversityPassword ? universityPassword : '••••••••••'}
                            </S.PasswordValue>
                            <S.PasswordToggle
                                role="button"
                                tabIndex={0}
                                aria-label={showUniversityPassword ? 'Hide password' : 'Show password'}
                                onClick={() => setShowUniversityPassword(prev => !prev)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setShowUniversityPassword(prev => !prev);
                                    }
                                }}>
                                <S.PasswordToggleIcon
                                    path={showUniversityPassword ? mdiEyeOffOutline : mdiEyeOutline}
                                    size={0.75}
                                />
                            </S.PasswordToggle>
                            {copied && <S.CopiedText>Copied</S.CopiedText>}
                        </S.PasswordValueRow>
                    </S.PasswordRow>
                </S.ResourceLinks>
                {demoModeEnabled && (
                    <>
                        <S.DemoModeIndicator>
                            <S.DemoModeText>Demo Mode</S.DemoModeText>
                        </S.DemoModeIndicator>
                    </>
                )}
            </S.SideBar>
        );
    }),
);
